import { EnvironmentInjector } from '@angular/core';
import BwipJs, { BwippOptions } from '@bwip-js/browser';
import {
  CrudFieldComponent,
  getLogger,
  getModelAndRepository,
  NgxComponentDirective,
  NgxEventHandler,
  presentNgxInlineModal,
} from '@decaf-ts/for-angular';
import { Batch, convertDateToGS1Format } from '@pharmaledgerassoc/ptp-toolkit/shared';

export async function createOnClickShowBarcodeModal(instance: CrudFieldComponent) {
  if (!instance.value) {
    instance.value = `<span class="ti ti-qrcode"></span> ${await instance.translate('batch.dataMatrix.view')}`;
  }
  const element = instance.component?.nativeElement as HTMLIonItemElement;
  if (element) {
    if (!instance._data || !Object.keys(instance._data as Batch)?.length) {
      if (!DatamatrixModalHandler.data) {
        const repository = getModelAndRepository(Batch.name)?.repository;
        if (repository) {
          DatamatrixModalHandler.data = (await repository?.read(instance.modelId)) as Batch;
        }
      }
      instance._data = DatamatrixModalHandler.data;
    }
    element.classList.add('dcf-has-action');
    element.onclick = () => {
      DatamatrixModalHandler.showBarcodeModal(instance, instance._data as Batch);
    };
  }
}

export const BarcodeTypes = {
  gs1datamatrix: 'gs1datamatrix',
  datamatrix: 'datamatrix',
  qrcode: 'qrcode',
} as const;

export class DatamatrixModalHandler extends NgxEventHandler {
  static data?: Batch;

  private static readonly charsMap: Record<string, string> = {
    '33': '!',
    '34': '"',
    '35': '#',
    '36': '$',
    '37': '%',
    '38': '&',
    '39': "'",
    '40': '(',
    '41': ')',
    '42': '*',
    '43': '+',
    '45': '-',
    '46': '.',
    '47': '/',
    '58': ':',
    '59': ';',
    '60': '<',
    '61': '=',
    '62': '>',
    '63': '?',
    '64': '@',
    '91': '[',
    '92': '\\',
    '93': ']',
    '94': '^',
    '95': '_',
    '96': '`',
    '123': '{',
    '124': '|',
    '125': '}',
    '126': '~',
  };

  private static readonly barcodeOptions = {
    text: '',
    includeBarcodeText: true,
    scale: 4,
    height: 16,
    textxalign: 'center',
    textyalign: 'center',
    backgroundcolor: 'ffffff',
    paddingwidth: 4,
    paddingheight: 4,
  } as BwippOptions;

  override async handleClick(
    instance: NgxComponentDirective,
    event: CustomEvent,
    uid: string,
    prop: string,
    model: Batch
  ) {
    const item = model || (instance._data as Batch[]).find((item) => item[instance.pk as keyof Batch] === uid);
    if (item) {
      DatamatrixModalHandler.showBarcodeModal(instance, item, this.injector);
    }
  }

  static async showBarcodeModal(
    instance: NgxComponentDirective,
    item: Batch,
    injector?: EnvironmentInjector
  ): Promise<void> {
    const datamatrixElement = DatamatrixModalHandler.getDatamatrixCanvasElement(item);
    //TODO: Create logic to make button copy value with correct lwa url to clipboard
    if (datamatrixElement) {
      const { productCode, batchNumber } = item;
      const title = (await instance.translate('batch.dataMatrix.preview', [productCode, batchNumber])) as string;
      await presentNgxInlineModal(
        datamatrixElement,
        {
          title,
          uid: 'dcf-datamatrix-modal',
          headerTransparent: true,
        },
        injector
      );
    }
  }

  static getBarcodeData(batch: Batch & { serialNumber?: string }): string {
    const { batchNumber, productCode, expiryDate, serialNumber } = batch || {};
    // TODO: Add serial number to batch and use it in datamatrix if exists (when added to model)
    const emptySerialNumber = serialNumber === '' || typeof serialNumber === 'undefined';
    const sanitizeBatchNumber = (value: string): string => {
      return value
        .split('')
        .map((char) => {
          if (this.charsMap[char.charCodeAt(0)]) {
            return Number(char.charCodeAt(0)) >= 100 ? `^${char.charCodeAt(0)}` : `^0${char.charCodeAt(0)}`;
          }
          return char;
        })
        .join('');
    };
    const gs1ExpiryDate = convertDateToGS1Format(`${expiryDate}`);
    return (
      emptySerialNumber
        ? `(01)${productCode}(10)${batchNumber}(17)${gs1ExpiryDate}`
        : `(01)${productCode}(21)${sanitizeBatchNumber(serialNumber)}(10)${sanitizeBatchNumber(batchNumber)}(17)${gs1ExpiryDate}`
    ).replace(/"/g, '\\"');
  }

  static getDatamatrixCanvasElement(
    batch: Batch,
    bcid: keyof typeof BarcodeTypes = BarcodeTypes.datamatrix
  ): HTMLElement | undefined {
    const barcodeData = this.getBarcodeData(batch);
    const container = document.createElement('div');
    container.setAttribute('style', 'width: 280px; height: 280px;');
    const options = {
      ...this.barcodeOptions,
      text: barcodeData,
      bcid,
    };
    try {
      const image = BwipJs.toSVG(options);
      container.innerHTML = image;
    } catch (error: unknown) {
      getLogger(this.getDatamatrixCanvasElement).error(
        `Invalid barcode data supplied: ${(error as Error).message || error}`
      );
    }
    return container;
  }
}
